import type {
  AssetDetail,
  AssetEntity,
  AssetLite,
  AssetTechnicalData,
  AssetTechnicalDataValid,
} from "./asset.type";
import type { Nullable } from "@/types";

import omitBy from "lodash/omitBy";

import { dateAddedDays, humanizeDays } from "@/utilities/date";
import { isEmptyObject, isValidObject } from "@/utilities/object";

export const getAssetTheoricalLifespan = (asset?: AssetEntity) => {
  const value = asset?.theoretical_lifespan_in_days;

  if (!value) return undefined;

  const text = humanizeDays(value);

  return {
    value,
    text,
    remaining: getAssetTheoricalRemainingLifespan(asset),
  };
};

export const getAssetTheoricalRemainingLifespan = (asset?: AssetEntity) => {
  const value = asset?.theoretical_remaining_lifespan_in_days;

  if (!value) return undefined;

  const text = humanizeDays(value, true);
  const date = dateAddedDays(value);

  return {
    value,
    text,
    date,
  };
};

export const getAssetCleanTechnicalData = (
  data: AssetTechnicalData
): AssetTechnicalData => {
  return omitBy(
    data,
    (value) =>
      value === null ||
      value === undefined ||
      (typeof value === "string" && value.trim() === "")
  ) as AssetTechnicalData;
};

export const isAssetValidTechnicalData = (
  data?: Nullable<AssetTechnicalData>
): data is AssetTechnicalDataValid => {
  return isValidObject(data);
};

export const getAssetValidTechnicalData = (
  data?: Nullable<AssetTechnicalData>
): AssetTechnicalDataValid => {
  return isAssetValidTechnicalData(data) ? data : {};
};

export const assetHasTechnicalData = (
  asset?: AssetDetail | AssetLite
): boolean => {
  const validData = getAssetValidTechnicalData(asset?.technical_data);
  return isEmptyObject(validData) ? false : true;
};
